import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { orderApi, adminConfigApi } from '../services/api'
import Button from '../components/common/Button'

/**
 * CheckoutSuccess — landing page after a successful checkout.
 *
 * Reads ?id=X from the URL, fetches that order, and shows:
 *   - Confirmation banner with the order id and totals
 *   - E-transfer instructions (amount + literature email + order #)
 *   - Pickup info (date + meeting location from admin config)
 *   - Backorder note if any
 *   - Item summary
 *   - Links to view orders / continue shopping
 *
 * Reachable directly via /checkout/success?id=42 — also navigable
 * later from the order history page.
 */
export default function CheckoutSuccess() {
    const [searchParams] = useSearchParams()
    const orderId = searchParams.get('id')

    const [order, setOrder] = useState(null)
    const [config, setConfig] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!orderId) {
            setLoading(false)
            return
        }
        loadOrder()
        loadConfig()
    }, [orderId])

    async function loadOrder() {
        try {
            const response = await orderApi.getById(orderId)
            if (response.success) setOrder(response.order)
            else toast.error(response.message || 'Could not load order')
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    async function loadConfig() {
        try {
            const response = await adminConfigApi.get()
            if (response.success) setConfig(response.config)
        } catch { /* non-fatal */ }
    }

    if (!orderId) {
        return (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center max-w-xl mx-auto">
                <h2 className="text-xl font-semibold text-ghasc-navy">No order specified</h2>
                <p className="text-gray-600 mt-2 text-sm">
                    The page was opened without an order id.
                </p>
                <div className="mt-4">
                    <Link to="/orders"><Button>View My Orders</Button></Link>
                </div>
            </div>
        )
    }

    if (loading) {
        return <div className="p-8 text-center text-gray-500">Loading order...</div>
    }

    if (!order) {
        return (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center max-w-xl mx-auto">
                <h2 className="text-xl font-semibold text-ghasc-navy">Order not found</h2>
                <p className="text-gray-600 mt-2 text-sm">
                    Couldn't find an order with id {orderId}.
                </p>
            </div>
        )
    }

    const totalFilled = order.items.reduce((sum, i) => sum + i.filledQuantity, 0)
    const totalBackordered = order.items.reduce((sum, i) => sum + i.backorderQuantity, 0)
    const litEmail = config?.literatureEmail
    const areaName = config?.areaName || 'the area'

    return (
        <div className="space-y-4 max-w-3xl mx-auto">
            {/* ----- Success banner ------------------------------------------------ */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-5xl mb-2">✓</div>
                <h2 className="text-2xl font-semibold text-green-900">Order submitted!</h2>
                <p className="text-green-800 mt-1">
                    Order #{order.id} for <strong>{order.customerName}</strong>
                </p>
                <p className="text-sm text-green-700 mt-2">
                    A confirmation email has been sent to you.
                </p>
            </div>

            {/* ----- Payment instructions ------------------------------------------ */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-ghasc-navy mb-3">Payment</h3>
                <div className="bg-amber-50 border-l-4 border-ghasc-tan p-4 rounded-r">
                    <p className="text-gray-800">
                        Please send <strong>${Number(order.subtotal).toFixed(2)}</strong> by e-transfer to:
                    </p>
                    {litEmail ? (
                        <p className="font-mono text-lg text-ghasc-navy mt-2">{litEmail}</p>
                    ) : (
                        <p className="text-amber-900 mt-2 text-sm">
                            (Literature email isn't set in Admin Config — please reach out to the chair.)
                        </p>
                    )}
                    <p className="text-sm text-gray-700 mt-2">
                        Include <strong>Order #{order.id}</strong> in the e-transfer message.
                    </p>
                </div>
            </div>

            {/* ----- Pickup info --------------------------------------------------- */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-ghasc-navy mb-3">Pickup</h3>
                <p className="text-gray-700">
                    Your order will be ready for pickup at the{' '}
                    <strong>{cycleToDisplay(order.cycleLabel)}</strong> area meeting.
                </p>
                {config?.datesTimes && (
                    <p className="text-sm text-gray-600 mt-2 whitespace-pre-line">
                        📅 {config.datesTimes}
                    </p>
                )}
            </div>

            {/* ----- Backorder note ----------------------------------------------- */}
            {order.backorderCount > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
                    <div className="font-semibold mb-1">⚠ Some items are backordered</div>
                    <p>
                        {totalFilled} item{totalFilled === 1 ? '' : 's'} will be ready for pickup at the
                        next meeting. {totalBackordered} item{totalBackordered === 1 ? '' : 's'} are
                        backordered and will be filled when the next regional shipment arrives. You'll
                        get them at a future meeting.
                    </p>
                </div>
            )}

            {/* ----- Items summary -------------------------------------------------- */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-ghasc-navy">Items</h3>
                </div>
                <table className="w-full">
                    <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <tr>
                            <th className="px-4 py-2">Item</th>
                            <th className="px-4 py-2 text-right w-16">Qty</th>
                            <th className="px-4 py-2 text-right w-20">Filled</th>
                            <th className="px-4 py-2 text-right w-24">Backorder</th>
                            <th className="px-4 py-2 text-right w-24">Total</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {order.items.map((line) => {
                            const lineTotal = Number(line.cost) * Number(line.requestedQuantity)
                            const variantLabel = [line.year, line.color].filter(Boolean).join(' / ')
                            return (
                                <tr key={line.id}>
                                    <td className="px-4 py-2">
                                        <div className="text-sm text-gray-900">{line.description}</div>
                                        <div className="text-xs text-gray-500 font-mono">
                                            {line.itemNumber}
                                            {variantLabel && <span className="ml-2 font-sans">— {variantLabel}</span>}
                                        </div>
                                    </td>
                                    <td className="px-4 py-2 text-right text-sm">{line.requestedQuantity}</td>
                                    <td className="px-4 py-2 text-right text-sm">{line.filledQuantity}</td>
                                    <td className="px-4 py-2 text-right text-sm">
                                        {line.backorderQuantity > 0
                                            ? <span className="text-amber-700">{line.backorderQuantity}</span>
                                            : '—'}
                                    </td>
                                    <td className="px-4 py-2 text-right text-sm">${lineTotal.toFixed(2)}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                        <tr>
                            <td colSpan="4" className="px-4 py-3 text-right text-sm font-medium text-gray-700">Total</td>
                            <td className="px-4 py-3 text-right text-lg font-semibold text-ghasc-navy">
                                ${Number(order.subtotal).toFixed(2)}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {/* ----- Next steps ----------------------------------------------------- */}
            <div className="flex flex-wrap gap-2 justify-center pt-4">
                <Link to="/orders"><Button variant="secondary">View My Orders</Button></Link>
                <Link to="/shop"><Button>Continue Shopping</Button></Link>
            </div>
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------
/**
 * Turn a cycle label like "2026-06" into "June 2026" for display.
 * Mirrors what OrderCycleService.CycleInfo.DisplayName produces server-side.
 */
function cycleToDisplay(label) {
    if (!label || label.length !== 7) return label || ''
    const year = parseInt(label.slice(0, 4), 10)
    const month = parseInt(label.slice(5), 10)
    if (isNaN(year) || isNaN(month)) return label
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December']
    return `${months[month - 1]} ${year}`
}